import { useTranslation } from "react-i18next";
import React from "react";
import Table from "../../../../components/Common/dataTable/index";
import { Button, Tooltip, message } from "antd";
import { ExamSession, UpdateExamSessionPayload } from "../../../../types/markTypes";
import ActionsDropdown, { Action } from "../../../../components/Common/ActionsDropdown";
import { formatToFrenchDate } from "utils/dateUtils";
import { useAbility } from "config/abilityContext";
import CustomButton from "components/Common/ui/CustomButton";
import { getSubjectLabelFromCode } from "utils/schoolUtils";

interface ExamSessionListProps {
  examSessions: ExamSession[];
  onEdit?: (examSession: UpdateExamSessionPayload) => void;
  onDelete?: (id: string) => void;
  onPublishMarks?: (examSessionId: string) => void; // ✅ New prop to trigger publish
  isPublishing?: boolean;
  loading?: boolean,
  dataPaginate: any;
  onChange: (pg: any) => void;
}

const getSubmissionIcon = (
  status: string) => {
  switch (status) {
    case "published":
      return <i className="ti ti-lock text-success" />;
    case "pending":
      return <i className="ti ti-check text-primary" />;
    case "incomplete":
      return <i className="ti ti-alert-circle text-warning" />;
    default:
      return <i className="ti ti-x text-danger" />;
  }
};

const ExamSessionList: React.FC<ExamSessionListProps> = ({ examSessions, onEdit, onDelete, onPublishMarks, isPublishing, loading, dataPaginate, onChange }) => {
  const { t } = useTranslation();
  const ability = useAbility();

  const columns = [
    { title: t("session"), dataIndex: "name", key: "name" },
    { title: t("semester"), dataIndex: "semester", key: "semester" },
    { title: t("start_date"), dataIndex: "startDate", key: "startDate", render: (text: string) => formatToFrenchDate(text) },
    // { title: t("end_date"), dataIndex: "endDate", key: "endDate", render: (text: string) => formatToFrenchDate(text) },
    { title: t("exam_type"), key: "examTypeId", render: (_: any, record: ExamSession) => record?.examType?.name },
    {
      title: t("subject"),
      key: "subjectName",
      render: (_: any, record: ExamSession) =>
        getSubjectLabelFromCode(record.subject?.code ?? ""),
    },
    {
      title: t("classes_&_status"),
      key: "classes",
      render: (_: any, record: ExamSession) => (
        <ul className="list-unstyled mb-0">
          {record?.classes?.length ? (
            record.classes.map((cls) => (
              <li key={cls.id} className="d-flex align-items-center gap-2">
                <Tooltip title={`Status: ${cls.submissionStatus}`}>
                  {getSubmissionIcon(cls.submissionStatus)}
                </Tooltip>
                <span>{cls?.name}</span>
              </li>
            ))
          ) : (
            <span className="text-muted">{t("no_classes")}</span>
          )}
        </ul>
      ),
    },
    {
      title: t("publish_marks"),
      key: "publish",
      render: (_: any, record: ExamSession) => {
        const allSubmitted = record.classes?.every(cls => cls.submissionStatus === "pending");
        const allPublished = record.classes?.every(cls => cls.submissionStatus === "published");

        return (
          ability.can("update", "ExamSession") ? (
            <Tooltip title={allPublished ? "Already Published" : allSubmitted ? "Publish Marks" : "Marks Not Ready"}>
              <CustomButton
                onClick={() => onPublishMarks?.(record.id)}
                disabled={!allSubmitted || isPublishing}
              >
                <i className="ti ti-send me-2" />
                {allPublished ? t("published") : t("publish")}
              </CustomButton>
            </Tooltip>
          ) : (
            <Tooltip title="You are not allowed to publish marks">
              <CustomButton
                disabled
              >
                <i className="ti ti-lock me-2" />
                {t("not_allowed")}
              </CustomButton>
            </Tooltip>
          )
        );
      },
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_: any, record: ExamSession) => {
        const isAnyPublished = record.classes?.some(cls => cls.submissionStatus === "published");

        const actions: Action[] = [];

        if (!isAnyPublished) {
          actions.push({
            label: t("edit"),
            icon: "ti ti-pencil",
            actionType: "link",
            hidden: !ability.can("update", "ExamSession"),
            onClick: () => {
              const payload: UpdateExamSessionPayload = {
                id: record.id,
                note: record.note,
                name: record.name,
                semester: record.semester,
                startDate: record.startDate,
                examTypeId: record.examTypeId,
                classIds: record.classes?.map(cls => cls.id) ?? [],
                examType: record?.examType ?? undefined,
                branchId: record?.classes?.[0]?.branchId ?? undefined,

              };
              onEdit?.(payload);
            },
          });
          actions.push({
            label: t("delete"),
            icon: "ti ti-trash",
            actionType: "modal",
            hidden: !ability.can("delete", "ExamSession"),
            onClick: () => onDelete?.(record.id),
          });
        } else {
          actions.push({
            label: `${t("edit")} (${t("locked")})`,
            icon: "ti ti-lock",
            actionType: "modal",
            onClick: () => {
              message.warning(t("cannot_edit_published_exam_session"));
            },
          });
          actions.push({
            label: `${t("delete")} (${t("locked")})`,
            icon: "ti ti-lock",
            actionType: "modal",
            onClick: () => {
              message.warning(t("cannot_delete_published_exam_session"));
            },
          });
        }

        return <ActionsDropdown actions={actions} />;
      },
    },
  ];

  return (
    <Table
      loading={loading}
      dataSource={examSessions}
      columns={columns}
      pagination={true}
      dataPaginate={dataPaginate}
      onChange={onChange}
    />
  );
};

export default ExamSessionList;
